import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { DashboardStats } from "@/types";

// Legacy mock functions (kept for backward compatibility)
export const fetchStats = async () => {
  // Simulate API call delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Current period stats
  const current = {
    totalUsers: 1245,
    totalBookSummaries: 378,
    activeSubscriptions: 845,
    revenueThisMonth: 28750,
  };

  // Previous period stats (for trend calculation)
  const previous = {
    totalUsers: 1112, // 12% growth
    totalBookSummaries: 350, // 8% growth
    activeSubscriptions: 735, // 15% growth
    revenueThisMonth: 23374, // 23% growth
  };

  // Calculate trends
  const trends = {
    usersTrend: Math.round(
      ((current.totalUsers - previous.totalUsers) / previous.totalUsers) * 100
    ),
    bookSummariesTrend: Math.round(
      ((current.totalBookSummaries - previous.totalBookSummaries) /
        previous.totalBookSummaries) *
        100
    ),
    subscriptionsTrend: Math.round(
      ((current.activeSubscriptions - previous.activeSubscriptions) /
        previous.activeSubscriptions) *
        100
    ),
    revenueTrend: Math.round(
      ((current.revenueThisMonth - previous.revenueThisMonth) /
        previous.revenueThisMonth) *
        100
    ),
  };

  return {
    ...current,
    trends,
  };
};

// Legacy function for backward compatibility
export const fetchPopularBookSummaries = async () => {
  // Get most viewed books from the database
  const { data, error } = await supabase
    .from("books")
    .select("id, title, view_count")
    .order("view_count", { ascending: false })
    .limit(5);

  if (error) {
    console.error("Error fetching popular books:", error);
    return [
      { id: 1, title: "Atomic Habits", views: 2458 },
      { id: 2, title: "Deep Work", views: 1957 },
      { id: 3, title: "Thinking, Fast and Slow", views: 1845 },
      { id: 4, title: "The Psychology of Money", views: 1654 },
    ];
  }

  // Transform the data to match our expected format
  return data.map((book) => ({
    id: book.id,
    title: book.title,
    views: book.view_count,
  }));
};

// Fetch last 5 recent subscribers (active) with profile and plan
export const fetchRecentSubscribers = async () => {
  const { data, error } = await supabase
    .from("user_subscriptions")
    .select(
      `
        id,
        user_id,
        subscription_start,
        created_at,
        status,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(id, name),
        profile:profiles!user_subscriptions_user_id_fkey(id, name, email, avatar_url)
      `
    )
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(5);

  if (error) {
    console.error("Error fetching recent subscribers:", error);
    return [] as Array<{
      id: string | number;
      name: string;
      email: string;
      plan?: string | null;
      subscriptionDate: string;
    }>;
  }

  return (
    data?.map((row) => ({
      id: row.id,
      name: row.profile?.name || "Unknown User",
      email: row.profile?.email || "",
      plan: row.subscription_plan?.name || null,
      subscriptionDate: row.subscription_start || row.created_at,
      avatarUrl: row.profile?.avatar_url || null,
    })) || []
  );
};

// Get recent users from database
export const fetchRecentUsers = async () => {
  // Query the profiles table for recent users
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, email, registered_at")
    .order("registered_at", { ascending: false })
    .limit(5);

  if (error) {
    console.error("Error fetching recent users:", error);
    throw error;
  }

  // Transform the data to match our expected format
  return data.map((user) => ({
    id: user.id,
    name: user.name,
    email: user.email,
    registeredAt: user.registered_at,
  }));
};

// Enhanced function that fetches comprehensive dashboard statistics
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  try {
    // Get count of total books
    const { count: bookCount, error: bookError } = await supabase
      .from("books")
      .select("*", { count: "exact", head: true });

    if (bookError) {
      console.error("Error fetching book count:", bookError);
      throw bookError;
    }

    // Get count of active books
    const { count: activeBookCount, error: activeBookError } = await supabase
      .from("books")
      .select("*", { count: "exact", head: true })
      .eq("is_active", true);

    if (activeBookError) {
      console.error("Error fetching active book count:", activeBookError);
      throw activeBookError;
    }

    // Get count of profiles (users)
    const { count: userCount, error: userError } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });

    if (userError) {
      console.error("Error fetching user count:", userError);
      throw userError;
    }

    // Get total number of users registered in the last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { count: newUserCount, error: newUserError } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .gte("registered_at", thirtyDaysAgo.toISOString());

    if (newUserError) {
      console.error("Error fetching new user count:", newUserError);
      throw newUserError;
    }

    // Get user growth data by month for the current year
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();

    const { data: usersByMonth, error: usersByMonthError } = await supabase
      .from("profiles")
      .select("registered_at")
      .gte("registered_at", startOfYear);

    if (usersByMonthError) {
      console.error("Error fetching users by month:", usersByMonthError);
      throw usersByMonthError;
    }

    // Process monthly user signups
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthlySignups = Array(12).fill(0);

    usersByMonth.forEach((user) => {
      const date = new Date(user.registered_at);
      const month = date.getMonth();
      monthlySignups[month]++;
    });

    const monthlySignupsData = months.map((month, index) => ({
      month,
      count: monthlySignups[index],
    }));

    // Get books created by month for the current year
    const { data: booksByMonth, error: booksByMonthError } = await supabase
      .from("books")
      .select("created_at")
      .gte("created_at", startOfYear);

    if (booksByMonthError) {
      console.error("Error fetching books by month:", booksByMonthError);
      throw booksByMonthError;
    }

    // Process monthly book creation
    const monthlyBooks = Array(12).fill(0);

    booksByMonth.forEach((book) => {
      const date = new Date(book.created_at);
      const month = date.getMonth();
      monthlyBooks[month]++;
    });

    const monthlyBooksData = months.map((month, index) => ({
      month,
      count: monthlyBooks[index],
    }));

    // Get popular books (most viewed)
    const { data: popularBooksData, error: popularBooksError } = await supabase
      .from("books")
      .select("id, title, view_count, cover_image, is_active")
      .order("view_count", { ascending: false })
      .limit(5);

    if (popularBooksError) {
      console.error("Error fetching popular books:", popularBooksError);
      throw popularBooksError;
    }

    // Get counts of summary contents by language
    const { data: languageData, error: languageError } = await supabase
      .from("summary_contents")
      .select("language");

    if (languageError) {
      console.error("Error fetching language data:", languageError);
      throw languageError;
    }

    // Count contents by language
    const languageCounts: Record<string, number> = {};
    languageData.forEach((content) => {
      if (content.language) {
        languageCounts[content.language] =
          (languageCounts[content.language] || 0) + 1;
      }
    });

    // Format as array of language stats
    const languageStats = Object.entries(languageCounts)
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count);

    // Get recent users
    const recentUsers = await fetchRecentUsers();

    // Get recent subscribers from user_subscriptions table (updated to match where active subscriptions are stored)
    const { data: recentSubscribersData, error: recentSubscribersError } =
      await supabase
        .from("user_subscriptions")
        .select(
          `
          id,
          user_id,
          subscription_start,
          created_at,
          subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
            id, name
          ),
          profile:profiles!user_subscriptions_user_id_fkey(
            id, name, email
          )
        `
        )
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(5);

    if (recentSubscribersError) {
      console.error(
        "Error fetching recent subscribers:",
        recentSubscribersError
      );
      // Don't throw, just use empty array as fallback
    }

    // Transform recent subscribers data
    const recentSubscribers =
      recentSubscribersData?.map((sub) => ({
        id: sub.id,
        name: sub.profile?.name || "Unknown User",
        email: sub.profile?.email || "",
        subscriptionDate: sub.subscription_start || sub.created_at,
        plan: sub.subscription_plan?.name,
      })) || [];

    // Get active subscriptions from user_subscriptions table (consistent with the rest of the app)
    const { count: subscriptionCount, error: subscriptionError } =
      await supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active")
        .lte("subscription_start", new Date().toISOString())
        .or(`subscription_end.is.null,subscription_end.gt.${new Date().toISOString()}`);

    // Default to 0 if error or table doesn't exist
    const activeSubscriptions = subscriptionError ? 0 : subscriptionCount || 0;

    // Get books with audio content
    const { data: audioBooks, error: audioBooksError } = await supabase
      .from("summary_contents")
      .select("book_id")
      .not("audio_url", "is", null)
      .not("audio_url", "eq", "");

    if (audioBooksError) {
      console.error("Error fetching audio books:", audioBooksError);
      throw audioBooksError;
    }

    // Count unique book IDs with audio
    const uniqueAudioBookIds = new Set();
    audioBooks.forEach((book) => {
      if (book.book_id) {
        uniqueAudioBookIds.add(book.book_id);
      }
    });

    const booksWithAudioCount = uniqueAudioBookIds.size;

    // Get total views across all books
    const { data: viewsData, error: viewsError } = await supabase
      .from("books")
      .select("view_count");

    if (viewsError) {
      console.error("Error fetching views data:", viewsError);
      throw viewsError;
    }

    const totalViews = viewsData.reduce(
      (sum, book) => sum + (book.view_count || 0),
      0
    );

    // Calculate monthly revenue (placeholder - adjust based on your actual revenue model)
    // For now, we'll estimate $5 per active subscription as a placeholder
    const estimatedRevenuePerSubscription = 5;
    const revenueThisMonth =
      activeSubscriptions * estimatedRevenuePerSubscription;

    // Calculate growth trends (comparing to previous period)
    // Get user count from previous period (60-30 days ago) to calculate real growth trend
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    const { count: previousPeriodUserCount, error: previousPeriodError } =
      await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .gte("registered_at", sixtyDaysAgo.toISOString())
        .lt("registered_at", thirtyDaysAgo.toISOString());

    if (previousPeriodError) {
      console.error(
        "Error fetching previous period user count:",
        previousPeriodError
      );
      // Don't throw error, just use fallback calculation
    }

    // Calculate user growth rate comparing current 30 days to previous 30 days
    const userAbsoluteChange = previousPeriodUserCount
      ? newUserCount - previousPeriodUserCount
      : newUserCount;

    const userGrowthRate =
      previousPeriodUserCount && previousPeriodUserCount > 0
        ? Math.round((userAbsoluteChange / previousPeriodUserCount) * 100)
        : userCount > 0
        ? Math.round((newUserCount / userCount) * 100)
        : 0;

    // Get count of categories
    const { count: categoriesCount, error: categoriesError } = await supabase
      .from("categories")
      .select("*", { count: "exact", head: true });

    if (categoriesError) {
      console.error("Error fetching categories count:", categoriesError);
      // Don't throw, just use 0 as fallback
    }

    // Get count of people
    const { count: peopleCount, error: peopleError } = await supabase
      .from("people")
      .select("*", { count: "exact", head: true });

    if (peopleError) {
      console.error("Error fetching people count:", peopleError);
      // Don't throw, just use 0 as fallback
    }

    // Get people role counts
    const { data: peopleRoles, error: peopleRolesError } = await supabase.from(
      "people_roles"
    ).select(`
        id, name,
        people_count:people_to_roles(count)
      `);

    if (peopleRolesError) {
      console.error("Error fetching people roles counts:", peopleRolesError);
      // Don't throw, just use empty array as fallback
    }

    // Transform the people roles data
    const peopleRolesCounts =
      peopleRoles
        ?.map((role) => {
          // Get the people count - safely handle undefined or null
          const count =
            Array.isArray(role.people_count) && role.people_count.length > 0
              ? role.people_count[0]?.count || 0
              : 0;

          return {
            name: role.name,
            count: count,
          };
        })
        .sort((a, b) => b.count - a.count) || [];

    // Get count of collections
    const { count: collectionsCount, error: collectionsError } = await supabase
      .from("collections")
      .select("*", { count: "exact", head: true });

    if (collectionsError) {
      console.error("Error fetching collections count:", collectionsError);
      // Don't throw, just use 0 as fallback
    }

    // Get count of sections
    const { count: sectionsCount, error: sectionsError } = await supabase
      .from("sections")
      .select("*", { count: "exact", head: true });

    if (sectionsError) {
      console.error("Error fetching sections count:", sectionsError);
      // Don't throw, just use 0 as fallback
    }

    // Get count of subscription plans
    const { count: subscriptionPlansCount, error: plansError } = await supabase
      .from("subscription_plans")
      .select("*", { count: "exact", head: true });

    if (plansError) {
      console.error("Error fetching subscription plans count:", plansError);
      // Don't throw, just use 0 as fallback
    }

    // Get count of total reads (this could be from a 'book_reads' or similar table)
    // For now, we'll query from a hypothetical "book_reads" table - adjust based on your actual schema
    const { count: totalReadsCount, error: totalReadsError } = await supabase
      .from("book_reads")
      .select("*", { count: "exact", head: true });

    if (totalReadsError) {
      console.error("Error fetching total reads count:", totalReadsError);
      // Don't throw - table might not exist in development environment
    }

    // Calculate additional growth trends

    // Get previous period subscription count
    const { count: previousSubscriptionCount, error: prevSubError } =
      await supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active")
        .lt("created_at", thirtyDaysAgo.toISOString());

    if (prevSubError) {
      console.error(
        "Error fetching previous subscription count:",
        prevSubError
      );
    }

    // Calculate subscription growth trend
    const subscriptionAbsoluteChange = previousSubscriptionCount
      ? activeSubscriptions - previousSubscriptionCount
      : activeSubscriptions;

    const subscriptionTrend =
      previousSubscriptionCount && previousSubscriptionCount > 0
        ? Math.round(
            (subscriptionAbsoluteChange / previousSubscriptionCount) * 100
          )
        : 0;

    // Get previous month's book count
    const previousMonthStart = new Date();
    previousMonthStart.setMonth(previousMonthStart.getMonth() - 1);
    previousMonthStart.setDate(1);
    previousMonthStart.setHours(0, 0, 0, 0);

    const currentMonthStart = new Date();
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);

    const { count: previousMonthBookCount, error: prevBookError } =
      await supabase
        .from("books")
        .select("*", { count: "exact", head: true })
        .gte("created_at", previousMonthStart.toISOString())
        .lt("created_at", currentMonthStart.toISOString());

    if (prevBookError) {
      console.error("Error fetching previous month book count:", prevBookError);
    }

    // Get current month's book count
    const { count: currentMonthBookCount, error: currBookError } =
      await supabase
        .from("books")
        .select("*", { count: "exact", head: true })
        .gte("created_at", currentMonthStart.toISOString());

    if (currBookError) {
      console.error("Error fetching current month book count:", currBookError);
    }

    // Calculate book trend
    const bookAbsoluteChange = previousMonthBookCount
      ? (currentMonthBookCount || 0) - previousMonthBookCount
      : currentMonthBookCount || 0;

    const bookTrend =
      previousMonthBookCount && previousMonthBookCount > 0
        ? Math.round((bookAbsoluteChange / previousMonthBookCount) * 100)
        : 0;

    // For revenue trend, we'll use the subscription trend as a proxy
    // In a real system, this would come from actual revenue data over time
    const revenueTrend = subscriptionTrend;

    // Return comprehensive dashboard stats
    return {
      // Core stats
      totalBooks: bookCount || 0,
      activeBooks: activeBookCount || 0,
      totalUsers: userCount || 0,
      newUsers: newUserCount || 0,
      activeSubscriptions,
      revenueThisMonth,
      booksWithAudio: booksWithAudioCount,
      totalViews,

      // Content counts
      categoriesCount: categoriesCount || 0,
      peopleCount: peopleCount || 0,
      peopleRolesCounts,
      collectionsCount: collectionsCount || 0,
      sectionsCount: sectionsCount || 0,
      totalReads: totalReadsCount || 0,
      subscriptionPlansCount: subscriptionPlansCount || 0,

      // Growth rates
      trends: {
        usersTrend: userGrowthRate,
        booksTrend: bookTrend,
        subscriptionsTrend: subscriptionTrend,
        revenueTrend: revenueTrend,
        usersAbsoluteChange: userAbsoluteChange,
        booksAbsoluteChange: bookAbsoluteChange,
        subscriptionsAbsoluteChange: subscriptionAbsoluteChange,
        revenueAbsoluteChange: subscriptionAbsoluteChange, // Using subscription change as proxy for revenue
      },

      // Distribution data
      languageStats,

      // Time series data
      monthlySignups: monthlySignupsData,
      monthlyBooks: monthlyBooksData,

      // Lists
      popularBooks: popularBooksData.map((book) => ({
        id: book.id,
        title: book.title,
        views: book.view_count,
        coverImage: book.cover_image,
        isActive: book.is_active,
      })),
      recentUsers,
      recentSubscribers,
    };
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);

    // Return placeholder data in case of error
    return {
      totalBooks: 0,
      activeBooks: 0,
      totalUsers: 0,
      newUsers: 0,
      activeSubscriptions: 0,
      revenueThisMonth: 0,
      booksWithAudio: 0,
      totalViews: 0,
      categoriesCount: 0,
      peopleCount: 0,
      peopleRolesCounts: [],
      collectionsCount: 0,
      sectionsCount: 0,
      totalReads: 0,
      subscriptionPlansCount: 0,
      trends: {
        usersTrend: 0,
        booksTrend: 0,
        subscriptionsTrend: 0,
        revenueTrend: 0,
        usersAbsoluteChange: 0,
        booksAbsoluteChange: 0,
        subscriptionsAbsoluteChange: 0,
        revenueAbsoluteChange: 0,
      },
      languageStats: [],
      monthlySignups: [],
      monthlyBooks: [],
      popularBooks: [],
      recentUsers: [],
      recentSubscribers: [],
    };
  }
};

// New focused API functions for split dashboards

// Overview Dashboard - Essential metrics only (fast loading)
export const fetchOverviewStats = async () => {
  try {
    // Get essential counts in parallel
    const nowIso = new Date().toISOString();
    const [
      { count: userCount },
      { count: bookCount },
      { count: activeBookCount },
      { count: subscriptionCount },
      { count: newUserCount }
    ] = await Promise.all([
      supabase.from("profiles").select("*", { count: "exact", head: true }),
      supabase.from("books").select("*", { count: "exact", head: true }),
      supabase.from("books").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active")
        .lte("subscription_start", nowIso)
        .or(`subscription_end.is.null,subscription_end.gt.${nowIso}`),
      supabase.from("profiles").select("*", { count: "exact", head: true }).gte("registered_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
    ]);

    // Get recent activity
    const { data: recentUsers } = await supabase
      .from("profiles")
      .select("id, name, email, avatar_url, registered_at")
      .order("registered_at", { ascending: false })
      .limit(3);

    // Calculate current month's revenue (subscriptions that started this month)
    const currentMonthStart = new Date();
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);
    
    const currentMonthEnd = new Date();
    currentMonthEnd.setHours(23, 59, 59, 999);
    
    const currentMonthRevenue = await calculateMonthlyRevenue(currentMonthStart, currentMonthEnd);

    // Calculate previous month's revenue for comparison
    const previousMonthStart = new Date();
    previousMonthStart.setMonth(previousMonthStart.getMonth() - 1);
    previousMonthStart.setDate(1);
    previousMonthStart.setHours(0, 0, 0, 0);

    const previousMonthEnd = new Date(previousMonthStart);
    previousMonthEnd.setMonth(previousMonthEnd.getMonth() + 1);
    previousMonthEnd.setDate(0); // Last day of previous month
    previousMonthEnd.setHours(23, 59, 59, 999);

    const previousMonthRevenue = await calculateMonthlyRevenue(previousMonthStart, previousMonthEnd);

    // Calculate month-over-month change
    const revenueChange = currentMonthRevenue - previousMonthRevenue;
    const revenueChangePercentage = previousMonthRevenue > 0 
      ? ((revenueChange / previousMonthRevenue) * 100)
      : currentMonthRevenue > 0 ? 100 : 0;

    return {
      totalUsers: userCount || 0,
      totalBooks: bookCount || 0,
      activeBooks: activeBookCount || 0,
      activeSubscriptions: subscriptionCount || 0,
      newUsers: newUserCount || 0,
      recentUsers: recentUsers || [],
      estimatedRevenue: Math.round(currentMonthRevenue * 100) / 100,
      previousMonthRevenue: Math.round(previousMonthRevenue * 100) / 100,
      revenueChange: Math.round(revenueChange * 100) / 100,
      revenueChangePercentage: Math.round(revenueChangePercentage * 100) / 100,
    };
  } catch (error) {
    console.error("Error fetching overview stats:", error);
    return {
      totalUsers: 0,
      totalBooks: 0,
      activeBooks: 0,
      activeSubscriptions: 0,
      newUsers: 0,
      recentUsers: [],
      estimatedRevenue: 0,
      previousMonthRevenue: 0,
      revenueChange: 0,
      revenueChangePercentage: 0,
    };
  }
};

// Helper function to calculate monthly revenue for a specific period
const calculateMonthlyRevenue = async (startDate?: Date, endDate?: Date) => {
  let query = supabase
    .from("user_subscriptions")
    .select(`
      price,
      currency_code,
      subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
        duration,
        duration_type
      )
    `)
    .eq("status", "active");

  // If date range is specified, filter by subscription start date
  if (startDate && endDate) {
    query = query
      .gte("subscription_start", startDate.toISOString())
      .lte("subscription_start", endDate.toISOString());
  }

  const { data: subscriptions } = await query;

  let totalRevenue = 0;
  
  if (subscriptions && subscriptions.length > 0) {
    subscriptions.forEach((subscription) => {
      const plan = subscription.subscription_plan;
      
      // Use the actual price from the subscription (what customer paid)
      let subscriptionPrice = subscription.price || 0;
      
      // Convert to INR if needed (current rate: 1 USD = 85 INR)
      if (subscription.currency_code === "USD") {
        subscriptionPrice = subscriptionPrice * 85;
      }

      // Convert to monthly equivalent for consistent revenue calculation
      let monthlyRevenue = subscriptionPrice;
      if (plan) {
        if (plan.duration_type === "months" && plan.duration > 1) {
          monthlyRevenue = subscriptionPrice / plan.duration;
        } else if (plan.duration_type === "years") {
          monthlyRevenue = subscriptionPrice / (plan.duration * 12);
        } else if (plan.duration_type === "days" && plan.duration > 30) {
          monthlyRevenue = subscriptionPrice / (plan.duration / 30);
        }
      }

      totalRevenue += monthlyRevenue;
    });
  }

  return totalRevenue;
};

// Content Analytics - Books, languages, categories, etc.
export const fetchContentAnalytics = async () => {
  try {
    const [
      { count: bookCount },
      { count: activeBookCount },
      { count: categoriesCount },
      { count: activeCategoriesCount },
      { count: collectionsCount },
      { count: activeCollectionsCount },
      { count: sectionsCount },
      { count: activeSectionsCount },
      { count: peopleCount },
      { data: languageData },
      { count: summaryContentsCount },
      { data: audioBooks },
      { data: bookMetricsData },
      { count: chaptersCount },
      { data: categoriesList },
      { data: booksCategoriesData },
      { data: popularBooksData },
      { data: mostReadBooksData },
      { data: mostPlayedBooksData },
      { data: peopleRolesData },
      { data: collectionsBooksData },
      recent30DaysCount,
      thisWeekCount,
      lastWeekCount,
      booksByMonthData,
      latestBooks
    ] = await Promise.all([
      supabase.from("books").select("*", { count: "exact", head: true }),
      supabase.from("books").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("categories").select("*", { count: "exact", head: true }),
      supabase.from("categories").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("collections").select("*", { count: "exact", head: true }),
      supabase.from("collections").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("sections").select("*", { count: "exact", head: true }),
      supabase.from("sections").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("people").select("*", { count: "exact", head: true }),
      supabase.from("summary_contents").select("language"),
      supabase.from("summary_contents").select("*", { count: "exact", head: true }),
      supabase.from("summary_contents").select("book_id").not("audio_url", "is", null).not("audio_url", "eq", ""),
      supabase.from("books").select("view_count, read_count, audio_play_count"),
      supabase.from("chapters").select("*", { count: "exact", head: true }),
      supabase.from("categories").select("id, name"),
      supabase.from("books_categories").select("category_id"),
      supabase.from("books").select("id, title, view_count, cover_image, is_active").order("view_count", { ascending: false }).limit(10),
      supabase.from("books").select("id, title, read_count, cover_image, is_active").order("read_count", { ascending: false }).limit(10),
      supabase.from("books").select("id, title, audio_play_count, cover_image, is_active").order("audio_play_count", { ascending: false }).limit(10),
      supabase.from("people_roles").select(`id, name, people_count:people_to_roles(count)`),
      supabase.from("collections_books").select("collection_id"),
      (async () => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const { count } = await supabase
          .from("books")
          .select("*", { count: "exact", head: true })
          .gte("created_at", thirtyDaysAgo.toISOString());
        return count || 0;
      })(),
      (async () => {
        const startOfThisWeek = new Date();
        startOfThisWeek.setHours(0, 0, 0, 0);
        startOfThisWeek.setDate(startOfThisWeek.getDate() - startOfThisWeek.getDay());
        const { count } = await supabase
          .from("books")
          .select("*", { count: "exact", head: true })
          .gte("created_at", startOfThisWeek.toISOString());
        return count || 0;
      })(),
      (async () => {
        const startOfThisWeek = new Date();
        startOfThisWeek.setHours(0, 0, 0, 0);
        startOfThisWeek.setDate(startOfThisWeek.getDate() - startOfThisWeek.getDay());
        const startOfLastWeek = new Date(startOfThisWeek);
        startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
        const { count } = await supabase
          .from("books")
          .select("*", { count: "exact", head: true })
          .gte("created_at", startOfLastWeek.toISOString())
          .lt("created_at", startOfThisWeek.toISOString());
        return count || 0;
      })(),
      (async () => {
        // Monthly content growth for current year
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
        const { data } = await supabase
          .from("books")
          .select("created_at")
          .gte("created_at", startOfYear);
        return data || [];
      })(),
      (async () => {
        const { data } = await supabase
          .from("books")
          .select("id, title, created_at")
          .order("created_at", { ascending: false })
          .limit(3);
        return data || [];
      })()
    ]);

    // Process language stats
    const languageCounts: Record<string, number> = {};
    languageData?.forEach((content) => {
      if (content.language) {
        languageCounts[content.language] = (languageCounts[content.language] || 0) + 1;
      }
    });

    const languageStats = Object.entries(languageCounts)
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count);

    // Count unique books with audio
    const uniqueAudioBookIds = new Set();
    audioBooks?.forEach((book) => {
      if (book.book_id) uniqueAudioBookIds.add(book.book_id);
    });

    // Aggregate engagement metrics
    const totalViews = bookMetricsData?.reduce((sum, book) => sum + (book.view_count || 0), 0) || 0;
    const totalReadsAcrossBooks = bookMetricsData?.reduce((sum, book) => sum + (book.read_count || 0), 0) || 0;
    const totalPlaysAcrossBooks = bookMetricsData?.reduce((sum, book) => sum + (book.audio_play_count || 0), 0) || 0;

    // Free books count
    const { count: freeBooksCount } = await supabase
      .from("books")
      .select("*", { count: "exact", head: true })
      .eq("is_free", true);

    // Top categories calculation
    const categoryCountMap: Record<number, number> = {};
    booksCategoriesData?.forEach((row: { category_id: number | null }) => {
      if (row.category_id != null) {
        categoryCountMap[row.category_id] = (categoryCountMap[row.category_id] || 0) + 1;
      }
    });
    type CategoryRow = { id: number; name: string };
    const categoriesById = new Map<number, string>();
    (categoriesList as CategoryRow[] | undefined)?.forEach((c) => categoriesById.set(c.id, c.name));
    const topCategories = Object.entries(categoryCountMap)
      .map(([id, count]) => ({ id: Number(id), name: categoriesById.get(Number(id)) || "Unknown", count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Build monthly content growth series
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthlyCounts = Array(12).fill(0);
    (booksByMonthData as Array<{ created_at: string }>).forEach((b) => {
      const d = new Date(b.created_at);
      monthlyCounts[d.getMonth()]++;
    });
    const contentGrowth = months.map((month, idx) => ({ month, count: monthlyCounts[idx] }));
    const avgSummaryContentsPerBook = (bookCount || 0) > 0
      ? Math.round(((summaryContentsCount || 0) / (bookCount || 1)) * 10) / 10
      : 0;

    // Collections book distribution stats
    const booksPerCollectionMap: Record<number, number> = {};
    (collectionsBooksData || []).forEach((row: { collection_id: number }) => {
      if (row.collection_id != null) {
        booksPerCollectionMap[row.collection_id] =
          (booksPerCollectionMap[row.collection_id] || 0) + 1;
      }
    });
    const collectionSizes = Object.values(booksPerCollectionMap);
    const avgBooksPerCollection = collectionSizes.length
      ? Math.round(
          (collectionSizes.reduce((a, b) => a + b, 0) / collectionSizes.length) *
            10
        ) / 10
      : 0;
    // Sections stats (books per section) via section_books table if present
    let avgBooksPerSection = 0;
    let minBooksPerSection = 0;
    let maxBooksPerSection = 0;
    try {
      const { data: sectionBooks } = await supabase
        .from("section_books")
        .select("section_id");
      if (sectionBooks) {
        const booksPerSectionMap: Record<string | number, number> = {};
        sectionBooks.forEach((row: { section_id: string | number }) => {
          if (row.section_id != null) {
            booksPerSectionMap[row.section_id] =
              (booksPerSectionMap[row.section_id] || 0) + 1;
          }
        });
        const sectionSizes = Object.values(booksPerSectionMap);
        if (sectionSizes.length) {
          const sum = sectionSizes.reduce((a, b) => a + b, 0);
          avgBooksPerSection = Math.round((sum / sectionSizes.length) * 10) / 10;
          minBooksPerSection = Math.min(...sectionSizes);
          maxBooksPerSection = Math.max(...sectionSizes);
        }
      }
    } catch (e) {
      // ignore missing table or query errors for optional stats
    }
    const minBooksPerCollection = collectionSizes.length
      ? Math.min(...collectionSizes)
      : 0;
    const maxBooksPerCollection = collectionSizes.length
      ? Math.max(...collectionSizes)
      : 0;

    // Transform people roles data to stable shape
    // people_roles selection returns people_count as an array of objects with a count field
    const peopleRolesCounts = (peopleRolesData || []).map(
      (r: { id: number; name: string; people_count: { count: number }[] }) => ({
        name: r.name,
        count: (r.people_count && r.people_count[0] && r.people_count[0].count) || 0,
      })
    );

    return {
      totalBooks: bookCount || 0,
      activeBooks: activeBookCount || 0,
      categoriesCount: categoriesCount || 0,
      activeCategoriesCount: activeCategoriesCount || 0,
      collectionsCount: collectionsCount || 0,
      sectionsCount: sectionsCount || 0,
      peopleCount: peopleCount || 0,
      peopleRolesCounts,
      booksWithAudio: uniqueAudioBookIds.size,
      totalViews,
      totalReadsAcrossBooks,
      totalPlaysAcrossBooks,
      freeBooksCount: freeBooksCount || 0,
      totalChapters: chaptersCount || 0,
      avgChaptersPerBook: (bookCount || 0) > 0 ? Math.round(((chaptersCount || 0) / (bookCount || 1)) * 10) / 10 : 0,
      avgSummaryContentsPerBook,
      totalSummaryContents: summaryContentsCount || 0,
      recentAdditions: recent30DaysCount || 0,
      thisWeekAdditions: thisWeekCount || 0,
      lastWeekAdditions: lastWeekCount || 0,
      topCategories,
      popularBooks: (popularBooksData || []).map((b: { id: number; title: string; view_count: number }) => ({ id: b.id, title: b.title, viewCount: b.view_count })) ,
      mostReadBooks: (mostReadBooksData || []).map((b: { id: number; title: string; read_count: number }) => ({ id: b.id, title: b.title, readCount: b.read_count })),
      mostPlayedBooks: (mostPlayedBooksData || []).map((b: { id: number; title: string; audio_play_count: number }) => ({ id: b.id, title: b.title, playCount: b.audio_play_count })),
      latestBooks: (latestBooks || []).map((b: { id: number; title: string; created_at: string }) => ({ id: b.id, title: b.title, created_at: b.created_at })),
      languageStats,
      contentGrowth,
      avgBooksPerCollection,
      minBooksPerCollection,
      maxBooksPerCollection,
      avgBooksPerSection,
      minBooksPerSection,
      maxBooksPerSection,
      activeCollectionsCount: activeCollectionsCount || 0,
      activeSectionsCount: activeSectionsCount || 0,
    };
  } catch (error) {
    console.error("Error fetching content analytics:", error);
    throw error;
  }
};

// User Analytics - User growth, registrations, etc.
// Global user metrics (not filtered by date range)
// Re-export user analytics functions for backward compatibility
export {
  fetchGlobalUserMetrics,
  fetchUserAnalytics,
  fetchEngagementTrends,
  fetchUserEngagementDetails,
} from "./user-analytics";

// Old implementations removed - now in user-analytics.ts

// System Analytics - System health, performance, database metrics
export const fetchSystemAnalytics = async () => {
  try {
    // Test database connection and measure response time
    const startTime = performance.now();
    
    // Simple database connection test
    const { data: connectionTest } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
    
    const endTime = performance.now();
    const responseTime = Math.round(endTime - startTime);

    // Mock data for system metrics (in a real system, these would come from monitoring services)
    return {
      systemHealth: "Healthy",
      databaseConnections: "Active",
      apiResponseTime: `${responseTime}ms`,
      uptime: "99.9%",
      totalTables: 15, // Static count - in real system would query system tables
      recentErrors: [], // Mock - would come from error tracking service
      cpuUsage: 23,
      memoryUsage: 45,
      storageUsage: 67,
      cacheHitRate: 94,
      requestRate: "1.2K",
      errorRate: "0.1%",
      loadBalancing: "Balanced"
    };
  } catch (error) {
    console.error("Error fetching system analytics:", error);
    // Return mock data in case of error
    return {
      systemHealth: "Unknown",
      databaseConnections: "Unknown",
      apiResponseTime: "Unknown",
      uptime: "Unknown",
      totalTables: 15,
      recentErrors: [],
      cpuUsage: 0,
      memoryUsage: 0,
      storageUsage: 0,
      cacheHitRate: 0,
      requestRate: "0",
      errorRate: "Unknown",
      loadBalancing: "Unknown"
    };
  }
};

// Content growth analytics (filtered by date range)
export const fetchContentGrowth = async (dateRange: { from: Date; to: Date }) => {
  try {
    if (!dateRange?.from || !dateRange?.to) {
      throw new Error("Date range is required");
    }

    const fromDate = dateRange.from;
    const toDate = dateRange.to;
    const fromISO = fromDate.toISOString();
    const toISO = toDate.toISOString();

    const { data: booksInRange } = await supabase
      .from("books")
      .select("created_at")
      .gte("created_at", fromISO)
      .lte("created_at", toISO);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Decide aggregation granularity similar to user analytics
    const isSameYear = fromDate.getFullYear() === toDate.getFullYear();
    const isSameMonth = isSameYear && fromDate.getMonth() === toDate.getMonth();
    const isSameDay = isSameMonth && fromDate.getDate() === toDate.getDate();

    let series: Array<{ month: string; count: number }> = [];

    if (isSameDay) {
      series = [
        {
          month: `${months[fromDate.getMonth()]} ${fromDate.getDate()}, ${fromDate.getFullYear()}`,
          count: booksInRange?.length || 0,
        },
      ];
    } else if (isSameMonth) {
      // Daily buckets for the month
      const daysInMonth = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0).getDate();
      const dailyCounts: number[] = Array(daysInMonth).fill(0);
      booksInRange?.forEach((b) => {
        const d = new Date(b.created_at as unknown as string);
        const day = d.getDate();
        dailyCounts[day - 1]++;
      });
      series = dailyCounts.map((count, idx) => ({ month: `${idx + 1}`, count }));
    } else if (isSameYear) {
      // Monthly buckets for the year
      const monthlyCounts: number[] = Array(12).fill(0);
      booksInRange?.forEach((b) => {
        const d = new Date(b.created_at as unknown as string);
        monthlyCounts[d.getMonth()]++;
      });
      series = months.map((m, idx) => ({ month: m, count: monthlyCounts[idx] }));
    } else {
      // Multi-year: month-year labels progressing from start to end
      const monthLabels: string[] = [];
      const monthCounts: number[] = [];
      const cursor = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
      const endCursor = new Date(toDate.getFullYear(), toDate.getMonth(), 1);
      while (cursor <= endCursor) {
        monthLabels.push(`${months[cursor.getMonth()]} ${cursor.getFullYear()}`);
        // Count entries that fall in this month
        const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);
        const count = (booksInRange || []).filter((b) => {
          const d = new Date(b.created_at as unknown as string);
          return d >= monthStart && d <= monthEnd;
        }).length;
        monthCounts.push(count);
        cursor.setMonth(cursor.getMonth() + 1);
      }
      series = monthLabels.map((label, idx) => ({ month: label, count: monthCounts[idx] }));
    }

    return { series };
  } catch (error) {
    console.error("Error fetching content growth:", error);
    return { series: [] as Array<{ month: string; count: number }> };
  }
};

// Old implementations removed - now in user-analytics.ts